<template src="./preview.html"></template>

<script>
import Vue from 'vue'
import array from '../../../../helpers/array'
import {clone} from "../../../../helpers/clone";
import { isEqual } from 'lodash'
import axios from "axios";
import moment from "moment";
import { quoteTaskCustomerDescription } from '../../../../helpers/calculatedQuoteDisplay'
import {
    calculateQuoteTotals,
    getQuoteLineTotal,
    getQuoteLineUnitPrice
} from '../../../../helpers/quoteTotals'
import { formatSuburbPostcodeStateLine } from '../../../../helpers/addressFormat'
import SignatureAcceptModal from '../../../common/SignatureAcceptModal.vue'
import SegmentControl from '../../../common/SegmentControl.vue'
import {
    DEFAULT_QUOTE_VALIDITY_DAYS,
    DEFAULT_INVOICE_DUE_DAYS,
    addDaysYmd,
    quoteValidityDaysFromSettings,
    invoiceDueDaysFromSettings
} from '../../../../helpers/documentDefaultDays'
import { formatDisplayDate } from '../../../../helpers/displayDate'
import { normalizeQuoteStatusId, coerceQuoteStatusFields } from '../../../../helpers/quoteStatus'

export default {
    components: {
        SignatureAcceptModal,
        SegmentControl
    },
    props: {
        parent_quote: {
            type: Object
        },
        parent_quote_items: {
            type: Array
        },
        parent_contacts: {
            type: Array
        },
        parent_contact: {
            type: Object,
            default: function () { return {}; }
        },
        parent_save: {
            type: Function
        },
        summary: {
            type: Boolean,
            default: false
        },
        read_only: {
            type: Boolean,
            default: false,
        },
        public_accept: {
            type: Boolean,
            default: false,
        },
        parent_settings: {
            type: Object,
            default: function () { return null; }
        },
        document_kind: {
            type: String,
            default: 'quote'
        }
    },
    watch: {
        quote: {
            handler: function () {
                // Vue 3: the immediate parent_quote_items watcher runs recalculateQuoteTotals
                // before mounted, mutating the default quote. Suppress the emit until the modal
                // has initialized from parent_quote, otherwise we overwrite the parent's quote
                // (losing number + line items) via @quote="quote=$event".
                if (!this.previewInitialized) return
                this.$emit('quote', this.quote)
            },
            deep: true
        },
        // Keep local date inputs stable; only refresh from quote when user hasn't edited.
        'quote.quote_date': function () {
            if (!this.dateChanged) this.syncLocalDatesFromQuote()
        },
        'quote.valid_until': function () {
            if (!this.dueDateChanged) this.syncLocalDatesFromQuote()
        },
        'quote.due_date': function () {
            if (!this.dueDateChanged) this.syncLocalDatesFromQuote()
        },
        parent_quote_items: {
            handler: function (val) {
                this.quoteItems = Array.isArray(val) ? val : [];
                this.recalculateQuoteTotals()
            },
            immediate: true
        },
        'quote.discount_amount_applied': function () {
            this.recalculateQuoteTotals()
        },
        'quote.discount_override': function () {
            this.recalculateQuoteTotals()
        },
        'quote.callout_fee': function () {
            this.recalculateQuoteTotals()
        },
        quoteItems: {
            handler: function () {
                this.recalculateQuoteTotals()
            },
            deep: true
        },
        'parent_quote.active_option_id': function () {
            if (!this.previewInitialized) return
            this.syncActiveOptionFromParent()
        },
        'parent_quote.active_option_index': function () {
            if (!this.previewInitialized) return
            this.syncActiveOptionFromParent()
        },
        'parent_quote.options': {
            handler: function () {
                if (!this.previewInitialized) return
                if (this.isInvoiceDocument) return
                if (this.parent_quote && this.parent_quote.options) {
                    this.$set(this.quote, 'options', this.parent_quote.options)
                }
                this.syncActiveOptionFromParent()
            },
            deep: true
        }
    },
    data: function () {
        return {
            previewInitialized: false,
            quote: {
                terms: null,
                status_id: 0,
            },
            quoteItems: [],
            contact: {},
            settings: {},
            discountOverride: 0,
            discounts: [],
            discountModalVisible: false,
            discountTab: 'list',
            selectedDiscountId: null,
            adhocDiscountAmount: '',
            quoteStatuses: [
                // {'id': 0, 'name': 'New'},
                {'id': 1, 'name': 'Draft'},
                {'id': 2, 'name': 'Sent'},
                {'id': 3, 'name': 'Accepted'},
                {'id': 5, 'name': 'Invoiced'},
                {'id': 6, 'name': 'Declined'},
            ],
            dateChanged: false,
            dueDateChanged: false,
            saving: false,
            issuedInput: '',
            expiryInput: '',
            signatureModalVisible: false,
            signatureCustomerName: '',
            signatureData: '',
            hasSignature: false,
            signatureSubmitting: false,
            pendingStatusBeforeAccept: null,
            signatureMode: 'plumber',
            statusSelectKey: 0,
            activeOptionIndex: 1,
            selectedAcceptOptionId: null,
            optionBaselines: {},
            _optionBaselineItems: {}
        }
    },
    computed: {
        quoteOptionsList: function () {
            const opts = (this.quote && this.quote.options) || (this.parent_quote && this.parent_quote.options) || []
            return Array.isArray(opts) ? opts.slice().sort((a, b) => a.option_index - b.option_index) : []
        },
        showOptionSegments: function () {
            // Invoices are one accepted package — never show Good/Better/Best option tabs
            if (this.isInvoiceDocument) return false
            return this.quoteOptionsList.length > 1
        },
        optionSegmentOptions: function () {
            return this.quoteOptionsList.map((o) => ({
                value: o.option_index,
                label: 'Option ' + o.option_index
            }))
        },
        activeOption: function () {
            return this.quoteOptionsList.find((o) => o.option_index === this.activeOptionIndex) || this.quoteOptionsList[0] || null
        },
        optionAcceptSelectItems: function () {
            return this.quoteOptionsList.map((o) => ({
                id: o.id,
                name: 'Option ' + o.option_index + ' - $' + (Number(o.total) || 0).toFixed(2)
            }))
        },
        canEditMetaFields: function () {
            return !this.read_only && this.currentStatusKey === 'draft'
        },
        canEditStatus: function () {
            return !this.read_only && (this.currentStatusKey === 'draft' || this.currentStatusKey === 'sent')
        },
        statusSelectItems: function () {
            if (this.isInvoiceDocument) {
                if (this.canEditStatus) {
                    return [
                        { id: 'draft', name: 'Draft' },
                        { id: 'sent', name: 'Sent' },
                        { id: 'approved', name: 'Approved' }
                    ]
                }
                const key = String((this.quote && this.quote.invoice_status) || 'draft').toLowerCase()
                const imap = { draft: 'Draft', sent: 'Sent', approved: 'Approved' }
                return [{ id: key, name: imap[key] || this.statusLabel || '—' }]
            }
            if (this.canEditStatus) return this.quoteStatuses
            const name = this.statusLabel || '—'
            const id = this.quote.status_id != null
                ? this.quote.status_id
                : this.statusIdFrom(this.currentStatusKey)
            return [{ id: id, name: name }]
        },
        statusSelectValue: function () {
            if (this.isInvoiceDocument) {
                return String((this.quote && this.quote.invoice_status) || 'draft').toLowerCase()
            }
            return this.quote.status_id
        },
        currentStatusKey: function () {
            if (this.isInvoiceDocument) {
                return String((this.quote && this.quote.invoice_status) || 'draft').toLowerCase()
            }
            const raw = this.quote && this.quote.status != null ? String(this.quote.status).toLowerCase() : ''
            if (raw) {
                if (raw === 'declined') return 'rejected'
                return raw
            }
            return this.statusFromId(this.quote && this.quote.status_id)
        },
        displayCompany: function () {
            return (this.parent_contact && this.parent_contact.company) || this.quote.contact_company || this.quote.contact_name || ''
        },
        displayAddressLines: function () {
            const q = this.quote || {}
            const p = this.parent_contact || {}
            const coalesce = function (a, b) {
                if (a != null && String(a).trim() !== '') return String(a).trim()
                if (b != null && String(b).trim() !== '') return String(b).trim()
                return ''
            }
            const c = {
                street_address: coalesce(p.street_address, q.contact_street_address),
                suburb: coalesce(p.suburb, q.contact_suburb),
                postcode: coalesce(p.postcode, q.contact_postcode),
                state: coalesce(p.state, q.contact_state)
            }
            const lines = []
            if (c.street_address) lines.push(c.street_address)
            const subLine = formatSuburbPostcodeStateLine(c.suburb, c.postcode, c.state)
            if (subLine) lines.push(subLine)
            if (lines.length) return lines
            const legacy = q.contact_address || ''
            return legacy ? [String(legacy)] : []
        },
        companySuburbStateLine: function () {
            const a = this.settings && this.settings.address ? this.settings.address : {}
            return formatSuburbPostcodeStateLine(a.suburb, a.post_code, a.state)
        },
        date: {
            // Quotes have an editable "Issued" date (quote_date). Fallback to created_at, then today.
            get: function () {
                if (this.issuedInput) return this.issuedInput
                const raw = this.isInvoiceDocument
                    ? (this.quote.invoice_date || this.quote.date || this.quote.created_at)
                    : (this.quote.quote_date || this.quote.date || this.quote.created_at)
                const m = moment(String(raw || ''), ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY'], true)
                if (m.isValid()) return m.format('YYYY-MM-DD')
                // ISO datetimes (e.g. created_at) — take calendar date only, no TZ shift via moment local
                const iso = /^(\d{4}-\d{2}-\d{2})/.exec(String(raw || ''))
                if (iso) return iso[1]
                return moment().format('YYYY-MM-DD')
            },
            set: function (value) {
                this.dateChanged = true
                this.issuedInput = value
                if (this.isInvoiceDocument) {
                    this.quote.invoice_date = value
                } else {
                    this.quote.quote_date = value
                }
            }
        },
        dueDate: {
            // Quotes use valid_until/due_date as expiry; invoices use invoice_due_date.
            // Defaults come from Display settings (quote_validity_days / invoice_due_days).
            get: function () {
                if (this.expiryInput) return this.expiryInput
                const raw = this.isInvoiceDocument
                    ? (this.quote.invoice_due_date || this.quote.due_date || this.quote.valid_until)
                    : (this.quote.due_date || this.quote.valid_until)
                const m = raw ? moment(String(raw), ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY'], true) : null
                if (m && m.isValid()) return m.format('YYYY-MM-DD')
                const iso = raw ? /^(\d{4}-\d{2}-\d{2})/.exec(String(raw)) : null
                if (iso) return iso[1]
                const baseRaw = this.isInvoiceDocument
                    ? (this.quote.invoice_date || this.quote.date || this.quote.created_at)
                    : (this.quote.quote_date || this.quote.date || this.quote.created_at)
                const days = this.isInvoiceDocument
                    ? invoiceDueDaysFromSettings(this.settings)
                    : quoteValidityDaysFromSettings(this.settings)
                return addDaysYmd(baseRaw, days)
            },
            set: function (value) {
                this.dueDateChanged = true
                this.expiryInput = value
                if (this.isInvoiceDocument) {
                    this.quote.invoice_due_date = value
                }
                this.quote.due_date = value
            }
        },
        statusLabel: function () {
            if (this.isInvoiceDocument) {
                const imap = { draft: 'Draft', sent: 'Sent', approved: 'Approved' }
                return imap[String(this.quote.invoice_status || 'draft').toLowerCase()] || 'Draft'
            }
            const map = { 1: 'Draft', 2: 'Sent', 3: 'Accepted', 5: 'Invoiced', 6: 'Declined', draft: 'Draft', sent: 'Sent', accepted: 'Accepted', rejected: 'Declined', invoiced: 'Invoiced' }
            return this.quote.status_name || map[this.quote.status_id] || map[this.quote.status] || ''
        },
        isInvoiceDocument: function () {
            // Only explicit invoice surfaces (e.g. public approve) use invoice chrome.
            // Quote list/builder must keep QUOTE chrome even when status is "invoiced".
            return String(this.document_kind || '').toLowerCase() === 'invoice'
        },
        signatureImageField: function () {
            return this.isInvoiceDocument ? 'invoice_signature_data' : 'signature_data'
        },
        signedByField: function () {
            return this.isInvoiceDocument ? 'invoice_signed_by_name' : 'signed_by_name'
        },
        signedAtField: function () {
            return this.isInvoiceDocument ? 'invoice_signed_at' : 'signed_at'
        },
        signatureImage: function () {
            if (!this.showSignatureForActiveOption) return ''
            return this.quote[this.signatureImageField] || ''
        },
        signedByValue: function () {
            if (!this.showSignatureForActiveOption) return ''
            return this.quote[this.signedByField] || ''
        },
        signedAtValue: function () {
            if (!this.showSignatureForActiveOption) return ''
            return this.quote[this.signedAtField] || ''
        },
        showSignatureForActiveOption: function () {
            const data = this.quote && this.quote[this.signatureImageField]
            if (!data) return false
            const acceptedId = this.quote.accepted_option_id
            if (acceptedId == null || acceptedId === '') return true
            if (!this.quoteOptionsList || this.quoteOptionsList.length <= 1) return true
            const active = this.activeOption
            if (!active) return false
            return Number(active.id) === Number(acceptedId)
        },
        documentTitle: function () {
            return this.isInvoiceDocument ? 'INVOICE' : 'QUOTE'
        },
        documentNumberLabel: function () {
            return this.isInvoiceDocument ? 'Invoice #' : 'Quote #'
        },
        dueDateLabel: function () {
            return this.isInvoiceDocument ? 'Due Date' : 'Expiry/Due'
        },
        customerCtaTitle: function () {
            return this.isInvoiceDocument ? 'Ready to approve?' : 'Ready to go ahead?'
        },
        customerCtaSteps: function () {
            if (this.isInvoiceDocument) {
                return [
                    'Review the details of your invoice are all correct.',
                    "When you're happy, hit Approve Invoice. You'll be prompted to sign and agree to our terms.",
                    "We'll mark your invoice as approved."
                ]
            }
            return [
                'Review the details of your quote are all correct.',
                "When you're happy, hit Accept Quote. You'll be prompted to sign and agree to our terms.",
                "We'll get your job booked in."
            ]
        },
        customerCtaButtonLabel: function () {
            return this.isInvoiceDocument ? 'Approve Invoice' : 'Accept Quote'
        },
        signatureCaptionVerb: function () {
            return this.isInvoiceDocument ? 'Approved' : 'Accepted'
        },
        discountDisplay: function () {
            const raw = this.quote && this.quote.discount_amount_applied != null
              ? this.quote.discount_amount_applied
              : (this.quote && this.quote.discount_override != null ? this.quote.discount_override : this.discountOverride)
            const n = parseFloat(raw)
            return isNaN(n) ? 0 : n
        },
        hasAppliedDiscount: function () {
            return this.discountDisplay > 0
        },
        discountDisplayNegative: function () {
            const n = this.discountDisplay
            return n > 0 ? -Math.abs(n) : 0
        },
        saveButtonText: function () {
            if (this.quote.id) {
                return 'Save & Close'
            } else {
                return 'Save as Draft'
            }
        },
        tasks: function () {
            let items = []
            this.quoteItems.forEach(function (item) {
                const categories = Array.isArray(item.categories) ? item.categories : []
                const firstId = categories[0] && categories[0].id
                if (item.item_type === 'task' && (firstId !== 36 && firstId !== 38)) {
                    items.push(item)
                }
            })
            return items
        },
        products: function () {
            let items = []
            this.quoteItems.forEach(function (item) {
                if (item.item_type === 'product') {
                    items.push(item)
                }
            })
            return items
        },
        fees: function () {
            let items = []
            this.quoteItems.forEach(function (item) {
                const categories = Array.isArray(item.categories) ? item.categories : []
                const firstId = categories[0] && categories[0].id
                if (item.item_type === 'task' && firstId === 36) {
                    items.push(item)
                }
            })
            return items
        },
        certs: function () {
            let items = []
            this.quoteItems.forEach(function (item) {
                const categories = Array.isArray(item.categories) ? item.categories : []
                const firstId = categories[0] && categories[0].id
                if (item.item_type === 'task' && firstId === 38) {
                    items.push(item)
                }
            })
            return items
        },
        allItems: function () {
            return [].concat(this.tasks, this.fees, this.products, this.certs)
        },
        // displayAddress kept for backwards compatibility; prefer displayAddressLines for Figma formatting
        displayAddress: function () {
            const c = this.parent_contact || {}
            const parts = []
            if (c.street_address) parts.push(c.street_address)
            if (c.suburb) parts.push(c.suburb)
            if (c.state && c.postcode) parts.push((c.state || '') + ' ' + (c.postcode || ''))
            return parts.length ? parts.join(' ') : (this.quote.contact_address || '')
        },
        /** En dash when missing; avoids showing literal "null" from API. */
        previewReferenceDisplay: function () {
            const r = this.quote && this.quote.reference
            if (r === null || r === undefined) return '\u2013'
            const s = String(r).trim()
            if (s === '' || s.toLowerCase() === 'null') return '\u2013'
            return s
        },
        defaultCustomerName: function () {
            const contact = this.parent_contact || {}
            const fromContact = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim()
            if (fromContact) return fromContact
            const fromQuote = String(this.quote.contact_name || '').trim()
            if (fromQuote) return fromQuote
            return String(this.displayCompany || '').trim()
        },
        signatureCompanyName: function () {
            return (this.settings && this.settings.business_name) ? String(this.settings.business_name).trim() : 'your provider'
        },
        signatureDisclaimer: function () {
            if (this.isInvoiceDocument) {
                return 'By signing, I approve this invoice and agree to all terms and conditions outlined by ' + this.signatureCompanyName + '.'
            }
            return 'By signing, I accept this quote and agree to all terms and conditions outlined by ' + this.signatureCompanyName + '.'
        },
        canSubmitSignature: function () {
            return String(this.signatureCustomerName || '').trim() !== '' && this.hasSignature && !!this.signatureData
        },
        showCustomerCta: function () {
            if (!this.public_accept) return false
            const status = this.currentStatusKey
            return status === 'sent'
        }
    },
    methods: {
        moment,
        formatDisplayDate,
        statusFromId: function (id) {
            const n = Number(id)
            const map = { 1: 'draft', 2: 'sent', 3: 'accepted', 5: 'invoiced', 6: 'rejected' }
            return map[n] || 'draft'
        },
        statusIdFrom: function (status) {
            const id = normalizeQuoteStatusId(status)
            return id != null ? id : 1
        },
        onStatusSelected: function ($event) {
            if (!this.canEditStatus || !$event || $event.id === '' || $event.id == null) return
            if (this.isInvoiceDocument) {
                const key = String($event.id).toLowerCase()
                if (!['draft', 'sent', 'approved'].includes(key)) return
                if (key === 'approved') {
                    this.pendingStatusBeforeAccept = {
                        status_id: this.quote.status_id,
                        status: this.quote.status,
                        invoice_status: this.quote.invoice_status
                    }
                    this.openSignatureModal('plumber')
                    return
                }
                this.$set(this.quote, 'invoice_status', key)
                return
            }
            const statusId = normalizeQuoteStatusId($event.id)
            if (statusId == null) return
            if (Number(statusId) === 3) {
                this.pendingStatusBeforeAccept = {
                    status_id: this.quote.status_id,
                    status: this.quote.status
                }
                this.openSignatureModal('plumber')
                return
            }
            this.quote.status_id = statusId
            this.quote.status = this.statusFromId(statusId)
        },
        formatSignedAt: function (val) {
            if (!val) return ''
            const m = moment(val)
            return m.isValid() ? m.format('D MMM YYYY [at] h:mm a') : ''
        },
        openSignatureModal: function (mode) {
            this.signatureMode = mode || 'plumber'
            this.signatureCustomerName = this.defaultCustomerName
            this.signatureData = ''
            this.hasSignature = false
            this.selectedAcceptOptionId = this.activeOption ? this.activeOption.id : null
            this.signatureModalVisible = true
            if (this.signatureMode === 'plumber') {
                this.statusSelectKey++
            }
            var self = this
            if (self.$refs.signatureModal && typeof self.$refs.signatureModal.prime === 'function') {
                self.$refs.signatureModal.prime()
            }
        },
        openSignatureModalForCustomer: function () {
            this.openSignatureModal('customer')
        },
        cancelSignatureModal: function () {
            this.signatureModalVisible = false
            this.signatureData = ''
            this.hasSignature = false
            if (this.signatureMode === 'plumber' && this.pendingStatusBeforeAccept) {
                this.quote.status_id = this.pendingStatusBeforeAccept.status_id
                this.quote.status = this.pendingStatusBeforeAccept.status
                this.pendingStatusBeforeAccept = null
                this.statusSelectKey++
            }
        },
        submitSignature: function () {
            if (!this.canSubmitSignature || this.signatureSubmitting) return
            const signedByName = String(this.signatureCustomerName || '').trim()
            const signatureData = String(this.signatureData || '').trim()
            if (this.signatureMode === 'customer') {
                this.signatureSubmitting = true
                const endpoint = this.isInvoiceDocument ? 'accept/invoice' : 'accept/quote'
                axios.post(endpoint, {
                    uuid: this.quote.id,
                    signed_by_name: signedByName,
                    signature_data: signatureData,
                    selected_option_id: this.selectedAcceptOptionId || (this.activeOption && this.activeOption.id) || null,
                    option_index: this.activeOptionIndex
                }).then((response) => {
                    this.signatureModalVisible = false
                    this.$emit('accepted', {
                        number: response.data.number,
                        total: response.data.total
                    })
                }).catch((err) => {
                    const msg = err.response && err.response.data && err.response.data.error
                        ? err.response.data.error
                        : (this.isInvoiceDocument ? 'Failed to approve invoice' : 'Failed to accept quote')
                    this.errorNotification && this.errorNotification(msg)
                }).finally(() => {
                    this.signatureSubmitting = false
                })
                return
            }

            this.$set(this.quote, this.signedByField, signedByName)
            this.$set(this.quote, this.signatureImageField, signatureData)
            this.$set(this.quote, 'active_option_id', this.selectedAcceptOptionId || (this.activeOption && this.activeOption.id) || null)
            this.$set(this.quote, 'selected_option_id', this.quote.active_option_id)
            this.quote.status_id = 3
            this.quote.status = 'accepted'
            const previousStatus = this.pendingStatusBeforeAccept
            this.pendingStatusBeforeAccept = null
            this.signatureSubmitting = true
            this.flushDatesToQuote()
            // Force one persistence pass so newly accepted signatures/status are saved
            // even though accepted quotes are normally locked for further edits.
            const p = typeof this.parent_save === 'function' ? this.parent_save(true, true, true) : Promise.resolve()
            Promise.resolve(p).then((payload) => {
                this.signatureModalVisible = false
                const saved = payload && payload.quote ? payload.quote : {}
                if (saved[this.signedByField]) this.$set(this.quote, this.signedByField, saved[this.signedByField])
                if (saved[this.signatureImageField]) this.$set(this.quote, this.signatureImageField, saved[this.signatureImageField])
                if (saved[this.signedAtField]) this.$set(this.quote, this.signedAtField, saved[this.signedAtField])
            }).catch((err) => {
                const msg = err.response && err.response.data && err.response.data.error
                    ? err.response.data.error
                    : 'Failed to save accepted quote'
                this.errorNotification && this.errorNotification(msg)
                if (previousStatus) {
                    this.quote.status_id = previousStatus.status_id
                    this.quote.status = previousStatus.status
                }
                this.$set(this.quote, this.signedByField, null)
                this.$set(this.quote, this.signatureImageField, null)
                this.$set(this.quote, this.signedAtField, null)
            }).finally(() => {
                this.signatureSubmitting = false
            })
        },
        syncLocalDatesFromQuote: function () {
            const issuedRaw = this.isInvoiceDocument
                ? (this.quote.invoice_date || this.quote.date || this.quote.created_at)
                : (this.quote.quote_date || this.quote.date || this.quote.created_at)
            const issuedStrict = moment(String(issuedRaw || ''), ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY'], true)
            let issuedValue
            if (issuedStrict.isValid()) {
                issuedValue = issuedStrict.format('YYYY-MM-DD')
            } else {
                const iso = /^(\d{4}-\d{2}-\d{2})/.exec(String(issuedRaw || ''))
                issuedValue = iso ? iso[1] : moment().format('YYYY-MM-DD')
            }

            const expiryRaw = this.isInvoiceDocument
                ? (this.quote.invoice_due_date || this.quote.due_date || this.quote.valid_until)
                : (this.quote.due_date || this.quote.valid_until)
            const expiryStrict = expiryRaw ? moment(String(expiryRaw), ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY'], true) : null
            let expiryValue = ''
            if (expiryStrict && expiryStrict.isValid()) {
                expiryValue = expiryStrict.format('YYYY-MM-DD')
            } else if (expiryRaw) {
                const iso = /^(\d{4}-\d{2}-\d{2})/.exec(String(expiryRaw))
                if (iso) expiryValue = iso[1]
            }
            if (!expiryValue) {
                const days = this.isInvoiceDocument
                    ? invoiceDueDaysFromSettings(this.settings)
                    : quoteValidityDaysFromSettings(this.settings)
                expiryValue = addDaysYmd(issuedValue, days)
            }

            if (!this.dateChanged) this.issuedInput = issuedValue
            if (!this.dueDateChanged) this.expiryInput = expiryValue
        },

        hideItem: function (item) {
            item.hidden = 1
        },
        getSettings: function () {
            if (this.public_accept && this.parent_settings) {
                this.settings = this.parent_settings
                if (!this.quote.terms && this.settings.quote_terms) {
                    this.quote.terms = this.settings.quote_terms
                }
                this.syncLocalDatesFromQuote()
                return
            }
            axios.get('/api/setting/display').then(response => {
                this.settings = response.data || {}
                if (!this.quote.terms && this.settings.quote_terms) {
                    this.quote.terms = this.settings.quote_terms
                }
                this.syncLocalDatesFromQuote()
            }).catch(() => {
                this.settings = {
                    quote_validity_days: DEFAULT_QUOTE_VALIDITY_DAYS,
                    invoice_due_days: DEFAULT_INVOICE_DUE_DAYS
                }
            })
        },
        getDiscounts: function () {
            axios.get('/api/discounts').then(response => {
                this.discounts = Array.isArray(response.data && response.data.discounts) ? response.data.discounts : []
            }).catch(() => {
                this.discounts = []
            })
        },
        openDiscountModal: function () {
            this.discountTab = 'list'
            this.selectedDiscountId = this.quote.discount_mode === 'preset' ? this.quote.discount_preset_id : null
            this.adhocDiscountAmount = this.quote.discount_mode === 'adhoc' ? String(this.quote.discount_amount_applied || '') : ''
            this.discountModalVisible = true
        },
        toggleDiscount: function (discount) {
            if (!discount) return
            const id = discount.id
            this.selectedDiscountId = this.selectedDiscountId == null ? id : (Number(this.selectedDiscountId) === Number(id) ? null : id)
        },
        /**
         * Recompute quote.sub_total / gst_total / total from current line items and discount.
         * Mirrors rightbar.calculateQuoteTotal so totals stay in sync regardless of how
         * the preview was opened (quote builder rightbar, quote list, etc.).
         */
        recalculateQuoteTotals: function () {
            const q = this.quote
            if (!q) return
            const totals = calculateQuoteTotals(q, this.quoteItems)
            this.$set(q, 'discount', totals.discount)
            this.$set(q, 'sub_total', totals.sub_total)
            this.$set(q, 'gst_total', totals.gst_total)
            this.$set(q, 'total', totals.total)
        },
        setQuoteDiscountPayload: function (payload) {
            const q = this.quote
            if (!q) return
            const keys = ['discount_mode', 'discount_preset_id', 'discount_title', 'discount_description', 'discount_type', 'discount_value', 'discount_amount_applied']
            keys.forEach((k) => {
                if (Object.prototype.hasOwnProperty.call(payload, k)) {
                    this.$set(q, k, payload[k])
                }
            })
            if (Object.prototype.hasOwnProperty.call(payload, 'discount_amount_applied')) {
                this.$set(q, 'discount_override', payload.discount_amount_applied)
            }
        },
        applyDiscountSelection: function () {
            if (this.discountTab === 'amount') {
                const amount = Math.max(0, Number(this.adhocDiscountAmount) || 0)
                this.setQuoteDiscountPayload({
                    discount_mode: amount > 0 ? 'adhoc' : null,
                    discount_preset_id: null,
                    discount_title: amount > 0 ? 'Amount Off' : null,
                    discount_description: null,
                    discount_type: amount > 0 ? 'amount' : null,
                    discount_value: amount > 0 ? amount : null,
                    discount_amount_applied: amount
                })
                this.recalculateQuoteTotals()
                this.persistCurrentOptionToCache()
                this.$emit('discount', {
                    discount_mode: this.quote.discount_mode,
                    discount_preset_id: this.quote.discount_preset_id,
                    discount_title: this.quote.discount_title,
                    discount_description: this.quote.discount_description,
                    discount_type: this.quote.discount_type,
                    discount_value: this.quote.discount_value,
                    discount_amount_applied: amount
                })
                this.discountModalVisible = false
                return
            }
            const sid = this.selectedDiscountId
            const selected = this.discounts.find(function (x) {
                return x != null && sid != null && String(x.id) === String(sid)
            })
            if (!selected) {
                this.setQuoteDiscountPayload({
                    discount_mode: null,
                    discount_preset_id: null,
                    discount_title: null,
                    discount_description: null,
                    discount_type: null,
                    discount_value: null,
                    discount_amount_applied: 0
                })
                this.recalculateQuoteTotals()
                this.persistCurrentOptionToCache()
                this.$emit('discount', { discount_amount_applied: 0 })
                this.discountModalVisible = false
                return
            }
            const subtotalBeforeDiscount = Number(this.quote.sub_total || 0) + Number(this.quote.discount || 0)
            const rawAmount = Number(selected.amount) || 0
            const amountApplied = selected.type === 'percentage'
                ? Math.max(0, (subtotalBeforeDiscount * rawAmount) / 100)
                : Math.max(0, rawAmount)
            const payload = {
                discount_mode: 'preset',
                discount_preset_id: selected.id,
                discount_title: selected.title,
                discount_description: selected.description || '',
                discount_type: selected.type,
                discount_value: rawAmount,
                discount_amount_applied: Math.round(amountApplied * 100) / 100
            }
            this.setQuoteDiscountPayload(payload)
            this.recalculateQuoteTotals()
            this.persistCurrentOptionToCache()
            this.$emit('discount', payload)
            this.discountModalVisible = false
        },
        deleteQuote: function () {
            const options = this.quoteOptionsList
            const active = this.activeOption
            const multi = options.length > 1 && active
            const title = 'Are you sure?'
            const statusNote = {
                accepted: ' This quote has been accepted; deleting it is permanent.',
                rejected: ' This quote has been declined; deleting it is permanent.',
                invoiced: ' This quote has been invoiced; deleting it is permanent.'
            }[this.currentStatusKey] || ''
            const text = (multi
                ? ('Delete Option ' + active.option_index + ' from quote ' + this.quote.number + '?')
                : ('Delete quote ' + this.quote.number)) + statusNote
            this.swal({
                title: title,
                text: text,
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                confirmButtonText: multi ? 'Yes, delete it!' : 'Yes, delete it!'
            }).then((result) => {
                if (!result.value) return
                if (multi) {
                    axios.delete('/quote/' + this.quote.id + '/options/' + active.id).then((response) => {
                        if (response.data && response.data.deleted_quote) {
                            this.$emit('close')
                            this.$router.push('/')
                            return
                        }
                        const opts = response.data.options || []
                        this.$set(this.quote, 'options', opts)
                        this.activeOptionIndex = 1
                        this.applyActiveOption()
                    }).catch((err) => {
                        const msg = err.response && err.response.data && err.response.data.error
                            ? err.response.data.error
                            : 'Failed to delete option'
                        this.errorNotification && this.errorNotification(msg)
                    })
                    return
                }
                axios.delete('/quote/' + this.quote.id, {id: this.quote.id}).then(() => {
                    this.$emit('close')
                    this.$router.push('/')
                })
            })
        },
        onAcceptOptionSelected: function (optionId) {
            this.selectedAcceptOptionId = optionId
            const opt = this.quoteOptionsList.find((o) => o.id === optionId)
            if (opt) {
                this.activeOptionIndex = opt.option_index
                this.applyActiveOption()
            }
        },
        syncActiveOptionFromParent: function () {
            if (this.isInvoiceDocument) return
            if (!this.quoteOptionsList.length) return
            // Prefer explicit active selection; fall back to accepted option; else Option 1
            let preferred = null
            if (this.quote.active_option_id) {
                preferred = this.quoteOptionsList.find((o) => o.id === this.quote.active_option_id)
            }
            if (!preferred && this.quote.active_option_index) {
                preferred = this.quoteOptionsList.find((o) => o.option_index === Number(this.quote.active_option_index))
            }
            if (!preferred && this.quote.accepted_option_id) {
                preferred = this.quoteOptionsList.find((o) => o.id === this.quote.accepted_option_id)
            }
            if (!preferred) preferred = this.quoteOptionsList[0]
            const nextIndex = preferred.option_index
            if (this.activeOptionIndex !== nextIndex) {
                this.activeOptionIndex = nextIndex
            }
            this.applyActiveOption()
        },
        applyActiveOption: function () {
            if (this.isInvoiceDocument) return
            const opt = this.activeOption
            if (!opt) return
            // Always clone so preview edits can't mutate the cached option / baseline by reference
            const items = clone(Array.isArray(opt.items) ? opt.items : [])
            this.quoteItems = items
            this.$set(this.quote, 'active_option_id', opt.id)
            this.$set(this.quote, 'active_option_index', opt.option_index)
            this.$set(this.quote, 'discount_mode', opt.discount_mode)
            this.$set(this.quote, 'discount_preset_id', opt.discount_preset_id)
            this.$set(this.quote, 'discount_title', opt.discount_title)
            this.$set(this.quote, 'discount_description', opt.discount_description)
            this.$set(this.quote, 'discount_type', opt.discount_type)
            this.$set(this.quote, 'discount_value', opt.discount_value)
            const applied = Number(opt.discount_amount_applied) || 0
            this.$set(this.quote, 'discount_amount_applied', applied)
            // Keep override in sync so a prior option's discount can't leak across options
            this.$set(this.quote, 'discount_override', applied)
            this.discountOverride = applied
            this.$set(this.quote, 'reference', opt.reference != null ? opt.reference : '')
            this.$set(this.quote, 'total', opt.total)
            this.$set(this.quote, 'total_amount', opt.total)
            this.recalculateQuoteTotals()
            this.$emit('items', items)
            this.$emit('option-change', opt)
            this.$emit('quote', this.quote)
        },
        normalizeItemsForDirtyCompare: function (items) {
            const list = Array.isArray(items) ? items : []
            return list.map((item) => {
                const qtyRaw = item.qty != null ? item.qty : item.quantity
                const qtyNum = Number(qtyRaw)
                const unit = item.sell_price != null ? item.sell_price : item.unit_price
                return {
                    item_type: item.item_type || null,
                    item_id: item.item_id != null ? item.item_id : null,
                    uuid: item.uuid != null ? item.uuid : null,
                    qty: Number.isFinite(qtyNum) ? qtyNum : 0,
                    unit_price: Number(unit) || 0,
                    description: item.description != null ? String(item.description) : '',
                    name: item.name != null ? String(item.name) : (item.item_name != null ? String(item.item_name) : '')
                }
            })
        },
        snapshotOptionState: function (opt, itemsOverride) {
            if (!opt) return null
            const items = itemsOverride != null ? itemsOverride : (Array.isArray(opt.items) ? opt.items : [])
            return {
                // total is derived from items + discount — omit to avoid false positives after recalculate
                items: this.normalizeItemsForDirtyCompare(items),
                reference: opt.reference != null ? String(opt.reference) : '',
                discount_mode: opt.discount_mode || null,
                discount_preset_id: opt.discount_preset_id != null ? Number(opt.discount_preset_id) : null,
                discount_title: opt.discount_title || null,
                discount_description: opt.discount_description || null,
                discount_type: opt.discount_type || null,
                discount_value: opt.discount_value != null ? Number(opt.discount_value) : null,
                discount_amount_applied: Number(opt.discount_amount_applied) || 0
            }
        },
        captureAllOptionBaselines: function () {
            const baselines = {}
            this.quoteOptionsList.forEach((opt) => {
                baselines[opt.id] = this.snapshotOptionState(opt)
            })
            this.optionBaselines = baselines
        },
        initOptionBaselinesFromSeed: function () {
            const seed = this.quote && this.quote._previewOptionBaselinesSource
            if (Array.isArray(seed) && seed.length) {
                const baselines = {}
                const itemMap = {}
                seed.forEach((opt) => {
                    if (opt && opt.id != null) {
                        baselines[opt.id] = this.snapshotOptionState(opt)
                        itemMap[opt.id] = clone(Array.isArray(opt.items) ? opt.items : [])
                    }
                })
                this.optionBaselines = baselines
                this._optionBaselineItems = itemMap
                this.$delete(this.quote, '_previewOptionBaselinesSource')
                return true
            }
            return false
        },
        captureBaselineForOption: function (opt) {
            if (!opt) return
            this.$set(this.optionBaselines, opt.id, this.snapshotOptionState(opt))
        },
        captureBaselineForCurrentWorkingState: function () {
            const opt = this.activeOption
            if (!opt) return
            this.$set(this.optionBaselines, opt.id, this.snapshotOptionState(Object.assign({}, opt, {
                reference: this.quote.reference,
                discount_mode: this.quote.discount_mode,
                discount_preset_id: this.quote.discount_preset_id,
                discount_title: this.quote.discount_title,
                discount_description: this.quote.discount_description,
                discount_type: this.quote.discount_type,
                discount_value: this.quote.discount_value,
                discount_amount_applied: this.quote.discount_amount_applied
            }), this.quoteItems))
        },
        getWorkingStateForOption: function (opt) {
            if (!opt) return null
            const isCurrent = this.activeOption && Number(opt.id) === Number(this.activeOption.id)
            if (isCurrent) {
                return this.snapshotOptionState(Object.assign({}, opt, {
                    reference: this.quote.reference,
                    discount_mode: this.quote.discount_mode,
                    discount_preset_id: this.quote.discount_preset_id,
                    discount_title: this.quote.discount_title,
                    discount_description: this.quote.discount_description,
                    discount_type: this.quote.discount_type,
                    discount_value: this.quote.discount_value,
                    discount_amount_applied: this.quote.discount_amount_applied
                }), this.quoteItems)
            }
            return this.snapshotOptionState(opt)
        },
        isOptionDirty: function (opt) {
            if (!opt) return false
            const baseline = this.optionBaselines[opt.id]
            if (!baseline) return false
            const current = this.getWorkingStateForOption(opt)
            return !isEqual(baseline, current)
        },
        isCurrentOptionDirty: function () {
            return this.isOptionDirty(this.activeOption)
        },
        getDirtyOptionIds: function () {
            this.persistCurrentOptionToCache()
            const ids = []
            this.quoteOptionsList.forEach((opt) => {
                if (this.isOptionDirty(opt)) ids.push(opt.id)
            })
            return ids
        },
        restoreOptionFromBaseline: function (opt) {
            if (!opt) return
            const baseline = this.optionBaselines[opt.id]
            if (!baseline) return
            opt.reference = baseline.reference
            opt.discount_mode = baseline.discount_mode
            opt.discount_preset_id = baseline.discount_preset_id
            opt.discount_title = baseline.discount_title
            opt.discount_description = baseline.discount_description
            opt.discount_type = baseline.discount_type
            opt.discount_value = baseline.discount_value
            opt.discount_amount_applied = baseline.discount_amount_applied
            // Keep full item objects for Discard restore
            if (this._optionBaselineItems && this._optionBaselineItems[opt.id]) {
                opt.items = clone(this._optionBaselineItems[opt.id])
            }
            this.$set(this.quote, 'options', this.quoteOptionsList)
            if (this.activeOption && Number(this.activeOption.id) === Number(opt.id)) {
                this.quoteItems = clone(opt.items || [])
                this.$set(this.quote, 'reference', baseline.reference)
                this.$set(this.quote, 'discount_mode', baseline.discount_mode)
                this.$set(this.quote, 'discount_preset_id', baseline.discount_preset_id)
                this.$set(this.quote, 'discount_title', baseline.discount_title)
                this.$set(this.quote, 'discount_description', baseline.discount_description)
                this.$set(this.quote, 'discount_type', baseline.discount_type)
                this.$set(this.quote, 'discount_value', baseline.discount_value)
                const applied = Number(baseline.discount_amount_applied) || 0
                this.$set(this.quote, 'discount_amount_applied', applied)
                this.$set(this.quote, 'discount_override', applied)
                this.discountOverride = applied
                this.recalculateQuoteTotals()
                this.$emit('items', this.quoteItems)
                this.$emit('option-change', opt)
            }
        },
        storeFullItemBaselines: function (optionsList) {
            const map = {}
            ;(optionsList || []).forEach((opt) => {
                if (opt && opt.id != null) {
                    map[opt.id] = clone(Array.isArray(opt.items) ? opt.items : [])
                }
            })
            this._optionBaselineItems = map
        },
        confirmDirtyOptionAction: function (label) {
            return this.swal({
                type: 'warning',
                title: 'Unsaved changes',
                text: label + ' has unsaved changes. Save or discard before continuing?',
                showCancelButton: true,
                confirmButtonText: 'Save',
                cancelButtonText: 'Discard',
                allowOutsideClick: false
            }).then((result) => {
                if (result.value) return 'save'
                if (result.dismiss === 'cancel') return 'discard'
                return 'cancel'
            })
        },
        saveDirtyOptionsSilent: function (optionIds) {
            if (typeof this.parent_save !== 'function') return Promise.resolve()
            const ids = Array.isArray(optionIds) && optionIds.length ? optionIds : this.getDirtyOptionIds()
            if (!ids.length) return Promise.resolve()
            return Promise.resolve(this.parent_save(true, true, false, ids)).then(() => {
                this.captureAllOptionBaselines()
                this.storeFullItemBaselines(this.quoteOptionsList)
            })
        },
        handleDirtyBeforeLeave: function (label, onContinue) {
            if (!this.isCurrentOptionDirty()) {
                onContinue()
                return
            }
            this.confirmDirtyOptionAction(label).then((action) => {
                if (action === 'cancel') return
                if (action === 'save') {
                    this.persistCurrentOptionToCache()
                    const ids = [this.activeOption.id]
                    this.saveDirtyOptionsSilent(ids).then(() => onContinue())
                    return
                }
                this.restoreOptionFromBaseline(this.activeOption)
                onContinue()
            })
        },
        requestDuplicate: function () {
            // Variation / duplicate APIs need a persisted quote id.
            this.persistCurrentOptionToCache()
            const needsCreate = !this.quote || this.quote.id == null || this.quote.id === ''
            const dirtyIds = needsCreate ? [] : this.getDirtyOptionIds()
            const proceed = () => {
                this.persistCurrentOptionToCache()
                const id = this.quote && this.quote.id
                if (id == null || id === '') {
                    this.errorNotification && this.errorNotification('Please save the quote before duplicating')
                    return
                }
                this.$emit('duplicate', id)
            }
            if (!needsCreate && !dirtyIds.length) {
                proceed()
                return
            }
            const label = needsCreate
                ? 'This quote'
                : (this.isCurrentOptionDirty()
                    ? ('Option ' + (this.activeOptionIndex || 1))
                    : 'This quote')
            this.confirmDirtyOptionAction(label).then((action) => {
                if (action === 'cancel') return
                if (action === 'save') {
                    this.persistCurrentOptionToCache()
                    const savePromise = needsCreate
                        ? (typeof this.parent_save === 'function'
                            ? Promise.resolve(this.parent_save(true, true, false, null))
                            : Promise.reject(new Error('Save unavailable')))
                        : this.saveDirtyOptionsSilent(dirtyIds)
                    Promise.resolve(savePromise).then(() => proceed()).catch((err) => {
                        const msg = (err && err.response && err.response.data && err.response.data.error)
                            || (err && err.message)
                            || 'Failed to save quote'
                        this.errorNotification && this.errorNotification(msg)
                    })
                    return
                }
                // Discard: drop local edits. Can't duplicate a quote that was never saved.
                if (needsCreate) return
                dirtyIds.forEach((id) => {
                    const opt = this.quoteOptionsList.find((o) => Number(o.id) === Number(id))
                    if (opt) this.restoreOptionFromBaseline(opt)
                })
                proceed()
            })
        },
        doOptionSegmentChange: function (idx) {
            this.activeOptionIndex = idx
            const nextOpt = this.quoteOptionsList.find((o) => o.option_index === idx)
            const landingClean = nextOpt ? !this.isOptionDirty(nextOpt) : true
            this.applyActiveOption()
            // After loading a clean option, re-baseline working state so recalculate
            // can't create a false dirty flag on the option we just entered.
            if (landingClean) {
                this.captureBaselineForCurrentWorkingState()
                if (this.activeOption) {
                    if (!this._optionBaselineItems) this._optionBaselineItems = {}
                    this.$set(this._optionBaselineItems, this.activeOption.id, clone(this.quoteItems))
                }
            }
        },
        onOptionSegmentChange: function (value) {
            const idx = parseInt(value, 10)
            if (!Number.isFinite(idx) || idx === this.activeOptionIndex) return
            this.handleDirtyBeforeLeave('Option ' + this.activeOptionIndex, () => {
                this.persistCurrentOptionToCache()
                this.doOptionSegmentChange(idx)
            })
        },
        requestClose: function () {
            this.handleDirtyBeforeLeave('Option ' + (this.activeOptionIndex || 1), () => {
                this.persistCurrentOptionToCache()
                this.$emit('close')
            })
        },
        onPreviewKeydown: function (e) {
            if (this.public_accept) return
            const key = e.key || e.code
            if (key !== 'Escape' && key !== 'Esc' && e.keyCode !== 27) return
            if (this.discountModalVisible || this.signatureModalVisible) return
            e.preventDefault()
            this.requestClose()
        },
        persistCurrentOptionToCache: function () {
            const opts = this.quoteOptionsList
            const current = opts.find((o) => o.option_index === this.activeOptionIndex)
            if (!current) return
            current.items = Array.isArray(this.quoteItems) ? clone(this.quoteItems) : []
            current.total = this.quote.total
            current.discount_mode = this.quote.discount_mode
            current.discount_preset_id = this.quote.discount_preset_id
            current.discount_title = this.quote.discount_title
            current.discount_description = this.quote.discount_description
            current.discount_type = this.quote.discount_type
            current.discount_value = this.quote.discount_value
            current.discount_amount_applied = this.quote.discount_amount_applied
            current.reference = this.quote.reference != null ? this.quote.reference : ''
            this.$set(this.quote, 'options', opts)
        },
        convertToInvoice () {
            const quoteId = this.quote && (this.quote.id != null ? this.quote.id : this.quote.uuid);
            if (!quoteId) {
                this.errorNotification && this.errorNotification('Please save the quote first');
                return;
            }
            // Skip save for accepted/rejected quotes (backend blocks updates to these statuses)
            const skipSave = this.quote.status === 'accepted' || this.quote.status === 'rejected';
            this.flushDatesToQuote()
            // Persist before convert; keep preview open (silent) and do not open send options.
            const savePromise = (!skipSave && typeof this.parent_save === 'function')
                ? Promise.resolve(this.parent_save(true, true))
                : Promise.resolve();
            savePromise.then(() => {
                const customer_display_name = this.displayCompany && String(this.displayCompany).trim()
                    ? String(this.displayCompany).trim()
                    : '';
                return axios.post(`/quote/${quoteId}/convert`, {
                    quote_id: quoteId,
                    customer_display_name
                });
            }).then((response) => {
                this.$emit('close');
                if (this.$route.path !== '/invoice') {
                    this.$router.push('/invoice').catch(function () {});
                }
            }).catch(err => {
                const msg = err.response && err.response.data && err.response.data.error ? err.response.data.error : (err.message || 'Failed to convert to invoice');
                this.errorNotification && this.errorNotification(msg);
            });
        },
        lineItemDescription (item) {
            return quoteTaskCustomerDescription(item)
        },
        getUnitPrice(item) {
            return getQuoteLineUnitPrice(item)
        },
        getLineTotal(item) {
            return getQuoteLineTotal(item)
        },
        flushDatesToQuote: function () {
            const fmts = ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY']
            const issuedSrc = this.issuedInput || (this.isInvoiceDocument ? this.quote.invoice_date : this.quote.quote_date) || this.quote.date || this.quote.created_at
            const mi = moment(String(issuedSrc || ''), fmts, true)
            if (mi.isValid()) {
                const y = mi.format('YYYY-MM-DD')
                if (this.isInvoiceDocument) {
                    this.quote.invoice_date = y
                } else {
                    this.quote.quote_date = y
                }
                this.quote.date = y
            }
            const expSrc = this.expiryInput || this.quote.invoice_due_date || this.quote.due_date || this.quote.valid_until
            const me = expSrc ? moment(String(expSrc), fmts, true) : null
            if (me && me.isValid()) {
                const y = me.format('YYYY-MM-DD')
                if (this.isInvoiceDocument) {
                    this.quote.invoice_due_date = y
                }
                this.quote.due_date = y
                this.quote.valid_until = y
            }
            // Backend persists quote terms in `notes`; keep both fields aligned from preview edits.
            if (this.quote.terms != null) {
                this.quote.notes = this.quote.terms
            }
        },
        onSaveClick: function () {
            this.flushDatesToQuote()
            this.persistCurrentOptionToCache()
            this.saving = true
            const dirtyIds = this.getDirtyOptionIds()
            const activeId = this.activeOption && this.activeOption.id
            // Only persist dirty options (+ current). Never blast all options in index
            // order — that made the last PUT (Option 3) overwrite last_active_option_id.
            let saveIds = dirtyIds.slice()
            if (activeId && !saveIds.some((id) => Number(id) === Number(activeId))) {
                saveIds.push(activeId)
            }
            if (!saveIds.length && activeId) saveIds = [activeId]
            const p = typeof this.parent_save === 'function'
                ? Promise.resolve(this.parent_save(true, false, false, saveIds))
                : Promise.resolve()
            Promise.resolve(p).then(() => {
                this.captureAllOptionBaselines()
                this.storeFullItemBaselines(this.quoteOptionsList)
            }).finally(() => { this.saving = false })
        },
        onSendClick: function () {
            this.flushDatesToQuote()
            this.$emit('send')
        },
        gotoQuote() {
            const proceed = () => {
                this.persistCurrentOptionToCache()
                if (this.activeOption) {
                    this.$set(this.quote, 'active_option_id', this.activeOption.id)
                    this.$set(this.quote, 'active_option_index', this.activeOption.option_index)
                    this.$emit('option-change', this.activeOption)
                    this.$emit('items', this.quoteItems)
                    try {
                        sessionStorage.setItem('smardie_quote_active_option', JSON.stringify({
                            quoteId: this.quote.id,
                            optionId: this.activeOption.id,
                            optionIndex: this.activeOption.option_index
                        }))
                    } catch (_) { /* ignore */ }
                }
                this.$emit('quote', this.quote)

                // Builder preview: just close
                if (!this.summary) {
                    this.$emit('close')
                    return
                }

                // List preview: navigate while overlay is still up so the list
                // never flashes underneath. beforeUnmount tears down the body portal.
                const optIdx = this.activeOption && this.activeOption.option_index
                const go = () => {
                    this.$router.push({
                        path: '/quote/' + this.quote.id,
                        query: optIdx ? { option: String(optIdx) } : {}
                    })
                }

                if (this.quote.status_id !== 1) {
                    this.swal({
                        type: 'warning',
                        title: 'Are you sure?',
                        showCancelButton: true,
                        confirmButtonColor: '#d33',
                        confirmButtonText: 'Edit',
                        text: `This quote has already been ${this.quote.status || 'sent'}, any change may cause issues when reconciling payment.`
                    }).then((result) => {
                        if (result.dismiss) {
                            return
                        }
                        go()
                    })
                    return
                }

                go()
            }

            this.handleDirtyBeforeLeave('Option ' + (this.activeOptionIndex || 1), proceed)
        }
    },
    mounted: function () {
        this.quote = this.parent_quote || {};
        this.quoteItems = Array.isArray(this.parent_quote_items) ? this.parent_quote_items : [];
        this.contact = this.parent_contact || {};
        const refRaw = this.quote.reference
        if (refRaw === null || refRaw === undefined || (typeof refRaw === 'string' && refRaw.trim().toLowerCase() === 'null')) {
            this.$set(this.quote, 'reference', '')
        }
        this.discountOverride = this.quote.discount_override;
        if (!this.quote.status_id && this.quote.status) {
            this.quote.status_id = this.statusIdFrom(this.quote.status)
        }
        coerceQuoteStatusFields(this.quote)
        if (!this.quote.status && this.quote.status_id) {
            this.quote.status = this.statusFromId(this.quote.status_id)
        }
        this.syncLocalDatesFromQuote()
        this.recalculateQuoteTotals()

        this.syncActiveOptionFromParent()

        if (!this.initOptionBaselinesFromSeed()) {
            this.captureAllOptionBaselines()
            this.storeFullItemBaselines(this.quoteOptionsList)
        }

        this.getSettings()
        if (!this.public_accept) {
            this.getDiscounts()
        }
        // Now safe to propagate quote edits back to the parent (see quote watcher guard).
        this.previewInitialized = true
        this._onPreviewKeydown = (e) => this.onPreviewKeydown(e)
        document.addEventListener('keydown', this._onPreviewKeydown)
        if (!this.public_accept) {
          document.documentElement.classList.add('document-preview-open')
          document.body.classList.add('document-preview-open')
          // Portal to body immediately so builder (under #sidebar) matches list:
          // fixed mask + Safari under-chrome fill must not be trapped by sidebar.
          const portalToBody = () => {
            if (this.$el && this.$el.parentNode && this.$el.parentNode !== document.body) {
              document.body.appendChild(this.$el)
            }
          }
          portalToBody()
          this.$nextTick(portalToBody)
        }
    },
    beforeUnmount: function () {
        if (this._onPreviewKeydown) {
            document.removeEventListener('keydown', this._onPreviewKeydown)
            this._onPreviewKeydown = null
        }
        document.documentElement.classList.remove('document-preview-open')
        document.body.classList.remove('document-preview-open')
        // Portaled to body in mounted — always detach so it cannot survive route changes
        if (this.$el && this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el)
        }
    }
}
</script>
